import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useApi from "../CustomHooks/useApi";
import { useToast } from "../../Contexts/ToastContext";
import { financeAPI } from "../../api/apiClient";
import ActionIcon from "../Trips/Participants/ActionIcon";
import "../../css/Finance/TripFinanceDetails.css";
import LoadingOverlay from "../../LoadingOverlay.js";
import ConfirmWithPhrasePopup from "../Common/ConfirmWithPhrasePopup.js";
import getParticipantStatusStyle from "../../utils/getParticipantStatusStyle.js";
import SettlementDraftSection from "./SettlementDraft/SettlementDraftSection.js";
import StatementVerificationSection from "./StatementVerification/StatementVerificationSection.js";

const TripFinanceDetails = () => {
  const { tripGuid } = useParams();
  const { callApi } = useApi();
  const { showToast, clearToast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [data, setData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [collapsedVariants, setCollapsedVariants] = useState({});
  const [expandedSettlementFact, setExpandedSettlementFact] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isTakingOver, setIsTakingOver] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSettling, setIsSettling] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [showSettleConfirm, setShowSettleConfirm] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);

  const settlementStatus = data?.settlementStatus ?? null;
  const showFinancialButtons = settlementStatus === 3 || data?.showFinancialButtons;

  const PARTICIPANT_STATUS = { PRESENT: 2, ABSENT: 3, CANCELLED: 4 };
  const getStatusIcon = (status) => getParticipantStatusStyle(status).icon;
  const getStatusClass = (status) => getParticipantStatusStyle(status).statusClass;
  const formatCurrency = (value) => `${Number(value ?? 0).toFixed(2)} zł`;

  const fetchReservations = () => {
    setIsInitialLoading(true);
    callApi(
      async () => financeAPI.getFinancialReservationsForTrip(tripGuid),
      async (result) => {
        setData(result ?? {});
        setIsInitialLoading(false);
      },
      () => {
        clearToast();
        setIsInitialLoading(false);
      }
    );
  };

  useEffect(() => { fetchReservations(); }, [tripGuid]);

  const grouped = useMemo(() => (data?.reservations ?? []).reduce((acc, item) => {
    if (!acc[item.variantGuid]) acc[item.variantGuid] = { variantName: item.variantName, reservations: [] };
    acc[item.variantGuid].reservations.push(item);
    return acc;
  }, {}), [data?.reservations]);

  const variantSummariesMap = useMemo(() => (data?.variantSummaries ?? []).reduce((acc, summary) => {
    acc[summary.variantGuid] = summary;
    return acc;
  }, {}), [data?.variantSummaries]);

  const getSettlementFacts = (group) => {
    const cancelled = group.reservations.flatMap((reservation) =>
      (reservation.participants ?? [])
        .filter((participant) => Number(participant.status) === PARTICIPANT_STATUS.CANCELLED)
        .map((participant) => ({ ...participant, reservationCode: reservation.reservationCode }))
    );

    const unusedInsurance = cancelled.map((participant) => {
      const nnw = Number(participant.nnwCost || 0) > 0 && !participant.hasNnwCoverage ? Number(participant.nnwCost || 0) : 0;
      const kr = Number(participant.krCost || 0) > 0 && !participant.hasKrCoverage ? Number(participant.krCost || 0) : 0;
      return { ...participant, nnw, kr, total: nnw + kr };
    }).filter((participant) => participant.total > 0);

    const recoveredTfg = cancelled.map((participant) => ({ ...participant, total: Number(participant.tfgCost || 0) }))
      .filter((participant) => participant.total > 0);

    const purchasedInsurance = cancelled.map((participant) => {
      const nnw = Number(participant.nnwCost || 0) > 0 && participant.hasNnwCoverage ? Number(participant.nnwCost || 0) : 0;
      const kr = Number(participant.krCost || 0) > 0 && participant.hasKrCoverage ? Number(participant.krCost || 0) : 0;
      return { ...participant, nnw, kr, total: nnw + kr };
    }).filter((participant) => participant.total > 0);

    return { unusedInsurance, recoveredTfg, purchasedInsurance };
  };

  const toggleSettlementFact = (variantGuid, type) => {
    const key = `${variantGuid}:${type}`;
    setExpandedSettlementFact((prev) => prev === key ? null : key);
  };

  const renderSettlementFact = ({ variantGuid, type, icon, title, description, items, kind }) => {
    if (!items.length) return null;
    const key = `${variantGuid}:${type}`;
    const isExpanded = expandedSettlementFact === key;
    const total = items.reduce((sum, item) => sum + Number(item.total || 0), 0);

    return (
      <div className={`settlement-fact settlement-fact--${kind || "neutral"}`}>
        <div className="settlement-fact-header" role="button" tabIndex={0} onClick={() => toggleSettlementFact(variantGuid, type)} onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleSettlementFact(variantGuid, type);
          }
        }}>
          <span className="settlement-fact-title">
            <span className="settlement-fact-icon"><i className={`fa-solid ${icon}`} /></span>
            <span className="settlement-fact-copy"><strong>{title}</strong><small>{description}</small></span>
          </span>
          <span className="settlement-fact-summary"><strong>{items.length}</strong> {items.length === 1 ? "osoba" : "osób"}<span className="settlement-fact-summary-separator" /><strong>{formatCurrency(total)}</strong><i className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`} /></span>
        </div>
        {isExpanded && (
          <div className="settlement-fact-details">
            {items.map((item) => (
              <div key={`${type}-${item.participantGuid}`} className="settlement-fact-person">
                <span className={`settlement-fact-person-status ${getStatusClass(item.status)}`} title="Status osoby"><i className={`fa-solid ${getStatusIcon(item.status)}`} /></span>
                <span className="settlement-fact-person-name">{item.name}</span>
                <small>{item.reservationCode}</small>
                <strong className="settlement-fact-person-total">{formatCurrency(item.total)}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const confirmTakeOver = () => {
    setShowConfirm(false);
    setIsTakingOver(true);
    callApi(() => financeAPI.changeSettlementStatus(tripGuid, 3), () => {
      showToast("Wycieczka została przejęta do rozliczenia", "success");
      setIsTakingOver(false);
      fetchReservations();
    }, () => { clearToast(); setIsTakingOver(false); });
  };

  const handleSettleTrip = () => {
    setIsSettling(true);
    callApi(() => financeAPI.changeSettlementStatus(tripGuid, 4), () => {
      showToast("Wycieczka została oznaczona jako rozliczona", "success");
      setIsSettling(false);
      fetchReservations();
    }, () => setIsSettling(false));
  };

  const handleRollbackToCoordinator = () => {
    setIsRollingBack(true);
    callApi(() => financeAPI.changeSettlementStatus(tripGuid, 1), () => {
      showToast("Wycieczka została cofnięta do osoby koordynującej", "success");
      setIsRollingBack(false);
      navigate("/finance");
    }, () => setIsRollingBack(false));
  };

  const renderActionButtons = () => {
    if (settlementStatus === 2) return <><ActionIcon icon={isTakingOver ? <i className="fa fa-spinner fa-spin" /> : <i className="fa-solid fa-person-digging" />} label={isTakingOver ? "Przejmowanie..." : "Przejmij do rozliczenia"} onClick={() => setShowConfirm(true)} disabled={isTakingOver} />{showConfirm && <ConfirmWithPhrasePopup phrase="okoń" text="Czy na pewno chcesz przejąć tę wycieczkę do rozliczenia?" onConfirm={confirmTakeOver} onCancel={() => setShowConfirm(false)} />}</>;

    if (showFinancialButtons) return <>
      <ActionIcon icon={isSettling ? <i className="fa fa-spinner fa-spin" /> : <i className="fa-solid fa-check-double" />} label={isSettling ? "Zatwierdzanie..." : "Zatwierdź rozliczenie"} onClick={() => setShowSettleConfirm(true)} disabled={isSettling || isRollingBack} />
      <ActionIcon icon={isRollingBack ? <i className="fa fa-spinner fa-spin" /> : <i className="fa-solid fa-rotate-left" />} label={isRollingBack ? "Cofanie..." : "Cofnij do os. koo"} onClick={() => setShowRollbackConfirm(true)} disabled={isSettling || isRollingBack} />
      <ActionIcon icon={<i className="fa-solid fa-chart-column" />} label="Przegląd finansowy" onClick={() => setActiveTab("overview")} disabled={isSettling || isRollingBack} />
      <ActionIcon icon={<i className="fa-solid fa-file-circle-check" />} label="Zweryfikuj wyciąg" onClick={() => setActiveTab("statementVerification")} disabled={isSettling || isRollingBack} />
      <ActionIcon icon={<i className="fa-solid fa-file-invoice-dollar" />} label="Szkic rozliczenia" onClick={() => setActiveTab("draft")} disabled={isSettling || isRollingBack} />
    </>;

    if (settlementStatus === 4) return <>
      <ActionIcon icon={<i className="fa-solid fa-chart-column" />} label="Przegląd finansowy" onClick={() => setActiveTab("overview")} disabled={isSettling || isRollingBack} />
      <ActionIcon icon={<i className="fa-solid fa-file-circle-check" />} label="Zweryfikuj wyciąg" onClick={() => setActiveTab("statementVerification")} disabled={isSettling || isRollingBack} />
      <ActionIcon icon={<i className="fa-solid fa-file-invoice-dollar" />} label="Szkic rozliczenia" onClick={() => setActiveTab("draft")} disabled={isSettling || isRollingBack} />
    </>;

    return null;
  };

  return (
    <div className="trip-finance-details">
      {data?.tripName && <h2 className="trip-finance-title">{data.tripName}</h2>}
      <div className="variant-action-icons">{renderActionButtons()}</div>

      {isInitialLoading ? <LoadingOverlay text="Ładowanie..." /> : !data ? (
        <div className="finance-error-message"><i className="fa-solid fa-triangle-exclamation" /> Nie udało się załadować danych.</div>
      ) : activeTab === "statementVerification" ? <StatementVerificationSection tripGuid={tripGuid} /> : activeTab === "draft" ? (
        <SettlementDraftSection tripGuid={tripGuid} financialData={data} onRefreshFinancialData={fetchReservations} />
      ) : (data?.reservations ?? []).length === 0 ? <div className="no-reservations">Brak rezerwacji do wyświetlenia.</div> : (
        Object.entries(grouped).map(([variantGuid, group]) => {
          const isCollapsed = collapsedVariants[variantGuid];
          const summary = variantSummariesMap[variantGuid];
          const facts = getSettlementFacts(group);
          const hasFacts = facts.unusedInsurance.length > 0 || facts.recoveredTfg.length > 0 || facts.purchasedInsurance.length > 0;

          return (
            <div key={variantGuid} className="variant-section">
              <div className="variant-section-header" onClick={() => setCollapsedVariants((prev) => ({ ...prev, [variantGuid]: !prev[variantGuid] }))}>
                <span className="financial-variant-name">
                  {group.variantName}
                  <span className="variant-balance-stats">(<i className="fa-solid fa-arrow-trend-up icon-up" /> {group.reservations.filter((r) => r.balance > 0).length}{" | "}<i className="fa-solid fa-arrow-trend-down icon-down" /> {group.reservations.filter((r) => r.balance < 0).length}{" | "}<i className="fa-solid fa-balance-scale" /> {group.reservations.filter((r) => r.balance === 0).length})</span>
                  {summary && <span className="variant-participant-status-stats">(<span className={getStatusClass(PARTICIPANT_STATUS.PRESENT)} title="Osoby obecne"><i className={`fa-solid ${getStatusIcon(PARTICIPANT_STATUS.PRESENT)}`} /> {summary.presentParticipantsCount}</span>{" | "}<span className={getStatusClass(PARTICIPANT_STATUS.ABSENT)} title="Osoby nieobecne"><i className={`fa-solid ${getStatusIcon(PARTICIPANT_STATUS.ABSENT)}`} /> {summary.absentParticipantsCount}</span>{" | "}<span className={getStatusClass(PARTICIPANT_STATUS.CANCELLED)} title="Osoby anulowane"><i className={`fa-solid ${getStatusIcon(PARTICIPANT_STATUS.CANCELLED)}`} /> {summary.cancelledParticipantsCount}</span>)</span>}
                </span>
                <span className="variant-toggle-icon">{isCollapsed ? "▼" : "▲"}</span>
              </div>

              {!isCollapsed && (
                <div className="variant-section-body">
                  {(summary || hasFacts) && <div className="variant-finance-overview">
                    {summary && <div className="variant-finance-summary">
                      <div className="variant-finance-summary-item"><span className="label">Suma wpływów:</span><span className="value">{formatCurrency(summary.totalIncomingTransfers)}</span></div>
                      <div className="variant-finance-summary-item"><span className="label">Dofinansowania:</span><span className="value">{formatCurrency(summary.totalSubsidies)}</span></div>
                      <div className="variant-finance-summary-item"><span className="label">Koszty NNW/KR:</span><span className="value">{formatCurrency(summary.totalNNWCosts)}</span></div>
                      <div className="variant-finance-summary-item"><span className="label">Suma zwrotów wykonanych:</span><span className="value">{formatCurrency(summary.totalOutgoingTransfers)}</span></div>
                    </div>}

                    {hasFacts && <div className="settlement-facts-box">
                      <div className="settlement-facts-heading"><i className="fa-solid fa-circle-info" /><div><strong>Informacje istotne przy rozliczeniu</strong><span>{facts.unusedInsurance.length + facts.recoveredTfg.length + facts.purchasedInsurance.length > 0 ? `${[facts.unusedInsurance, facts.recoveredTfg, facts.purchasedInsurance].filter((items) => items.length > 0).length} sprawy do uwzględnienia` : ""}</span></div></div>
                      {renderSettlementFact({ variantGuid, type: "unusedInsurance", icon: "fa-shield-halved", title: "Niewykupione NNW / KR anulowanych osób", description: "Koszt niewykupionego ubezpieczenia; do zwrotu os. ucz.", items: facts.unusedInsurance, kind: "warning" })}
                      {renderSettlementFact({ variantGuid, type: "tfg", icon: "fa-landmark", title: "Odzyskane TFG anulowanych osób", description: "Odzyskana składka TFG; do zwrotu os. ucz.", items: facts.recoveredTfg, kind: "success" })}
                      {renderSettlementFact({ variantGuid, type: "purchasedInsurance", icon: "fa-shield", title: "Wykupione NNW / KR anulowanych osób", description: "Koszt wykupienia ubezpieczenia os. anul.; nie podlega zwrotowi.", items: facts.purchasedInsurance, kind: "info" })}
                    </div>}
                  </div>}

                  <div className="variant-table-wrapper">
                    <table className="variant-reservation-table">
                      <thead><tr><th>Rezerwacja</th><th className="participants-status-header" title="Liczba osób obecnych"><span className={getStatusClass(PARTICIPANT_STATUS.PRESENT)}><i className={`fa-solid ${getStatusIcon(PARTICIPANT_STATUS.PRESENT)}`} /></span></th><th className="participants-status-header" title="Liczba osób nieobecnych"><span className={getStatusClass(PARTICIPANT_STATUS.ABSENT)}><i className={`fa-solid ${getStatusIcon(PARTICIPANT_STATUS.ABSENT)}`} /></span></th><th className="participants-status-header" title="Liczba osób anulowanych"><span className={getStatusClass(PARTICIPANT_STATUS.CANCELLED)}><i className={`fa-solid ${getStatusIcon(PARTICIPANT_STATUS.CANCELLED)}`} /></span></th><th>Koszt</th><th>Wpłaty</th><th>Zwroty</th><th>Saldo (faktyczne)</th><th>Saldo (widoczne)</th></tr></thead>
                      <tbody>
                        {group.reservations.slice().sort((a, b) => (a.primaryParticipantName || "").localeCompare(b.primaryParticipantName || "", "pl", { sensitivity: "base" })).map((r) => (
                          <React.Fragment key={r.reservationCode}>
                            <tr className={`clickable ${expandedRow === r.reservationCode ? "expanded-row" : ""}`} onClick={() => setExpandedRow(expandedRow === r.reservationCode ? null : r.reservationCode)}>
                              <td><span className={`reg-code status-${r.status}`}>{r.reservationCode}</span><span className="primary-participant-name">{r.primaryParticipantName}</span></td>
                              <td className="participants-status-count"><span className={getStatusClass(PARTICIPANT_STATUS.PRESENT)}>{r.presentParticipantsCount ?? 0}</span></td>
                              <td className="participants-status-count"><span className={getStatusClass(PARTICIPANT_STATUS.ABSENT)}>{r.absentParticipantsCount ?? 0}</span></td>
                              <td className="participants-status-count"><span className={getStatusClass(PARTICIPANT_STATUS.CANCELLED)}>{r.cancelledParticipantsCount ?? 0}</span></td>
                              <td>{formatCurrency(r.realDue)}</td><td>{formatCurrency(r.incomingTransfers)}</td><td>{formatCurrency(r.outgoingTransfers)}</td>
                              <td><strong className={r.balance > 0 ? "balance-positive" : r.balance < 0 ? "balance-negative" : "balance-zero"}>{formatCurrency(r.balance)}</strong></td>
                              <td><div className={r.visibleBalance > 0 ? "balance-positive" : r.visibleBalance < 0 ? "balance-negative" : "balance-zero"}>({formatCurrency(r.visibleBalance)})</div></td>
                            </tr>
                            {expandedRow === r.reservationCode && <tr className="expanded-participants-row"><td colSpan={9}><div className="reservation-participant-details"><div className="participant-table-wrapper"><table className="participant-table"><thead><tr><th>Imię i nazwisko</th><th>Koszt bazowy</th><th>Korekty</th><th>Dofinansowanie</th><th>Realny koszt</th></tr></thead><tbody>{r.participants.map((p, idx) => <tr key={idx}><td><span className={`participant-name-icon ${getParticipantStatusStyle(p.status).statusClass}`}><i className={`fa-solid ${getParticipantStatusStyle(p.status).icon}`} /></span><span className={getParticipantStatusStyle(p.status).strikeThrough ? "strike-through" : ""}>{p.name}</span></td><td>{formatCurrency(p.baseCost)}</td><td>{formatCurrency(p.correctionAmount)}</td><td>{formatCurrency(p.subsidyAmount)}</td><td>{formatCurrency(p.realCost)}</td></tr>)}</tbody></table></div></div></td></tr>}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {showSettleConfirm && <ConfirmWithPhrasePopup phrase="okoń" text="Czy na pewno chcesz zatwierdzić rozliczenie wycieczki?" onConfirm={() => { setShowSettleConfirm(false); handleSettleTrip(); }} onCancel={() => setShowSettleConfirm(false)} />}
      {showRollbackConfirm && <ConfirmWithPhrasePopup phrase="okoń" text="Czy na pewno chcesz cofnąć wycieczkę do osoby koordynującej?" onConfirm={() => { setShowRollbackConfirm(false); handleRollbackToCoordinator(); }} onCancel={() => setShowRollbackConfirm(false)} />}
    </div>
  );
};

export default TripFinanceDetails;
